import { ActionReducerMapBuilder, Draft, createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import {
    urlParamReplicaId,
    urlWorkflowForward,
    urlWorkflowNext,
    urlWorkflowPasswordValidation,
    urlWorkflowRelease,
    urlWorkflowTaskReply,
} from '../../Constants';
import { IAction, IAppReduxStore, SliceProps } from '../store';
import { safeJsonParse } from '../../util/DataUtils';
import { GlobalVariables } from '../../GlobalVariables';
import { resetUtilsFileUploadState, uploadFiles } from '../general/sliceFileUpload';
import { resetFileState as resetFileUploadState } from '../import/sliceImportFileUpload';
import { IAdditionalInfoResultButtonEnum } from '../../components/General/Dialogs/_parts/AdditionalInputFooterActions';
import { IDynamicActionReplyArguments } from './documents/sliceProjectDocumentsDynamicSelection';
import { apiFetch } from '../../util/ProjectUtils';

const getDocumentTitle = ({
    docNumber,
    docSubject = '',
    docIndex,
}: {
    docNumber: string;
    docSubject?: string;
    docIndex: string;
}) => {
    if (docNumber) {
        const suffix = docIndex.trim() ? ` - ${docIndex}` : '';
        return `${docNumber}${suffix}`;
    }
    return docSubject;
};

const addPath = (item: WorkflowOption, path: string): WorkflowOption => {
    item = {
        ...item,
        path: path ?? '',
        connectorSubSelection: item.connectorSubSelection.map((_el, i) => {
            return {
                ...addPath(_el, `${path}.connectorSubSelection[${i}]`),
            };
        }),
    };

    return item;
};

const addPathInitialRelease = (item: InitialReleaseOption, path: string): InitialReleaseOption => {
    const finalOptions: InitialReleaseFinalOption[] = [];

    item?.workflowTask?.forEach((_el, i) => {
        const finalOption: InitialReleaseFinalOption = {
            path: `${path}.workflowTask[${i}]`,
            workflowId: item.workflowId,
            workflowTaskId: _el.workflowTaskId,
            workflowTaskTitle: _el.workflowTaskTitle,
        };

        if (_el.workflowTaskDynamicRole?.length) {
            const roleOptions: InitialReleaseRoleOption[] = _el.workflowTaskDynamicRole.map(
                role => ({
                    path: `${path}.workflowTask[${i}].workflowTaskDynamicRole[${role}]`,
                    workflowId: item.workflowId,
                    workflowTaskId: _el.workflowTaskId,
                    workflowTaskTitle: _el.workflowTaskTitle,
                    role,
                }),
            );

            finalOption.role = roleOptions;
        }

        finalOptions.push(finalOption);
    });

    return {
        ...item,
        path: path ?? '',
        workflowTask: finalOptions,
    };
};

const getFilteredOptions = (
    options: WorkflowOption[],
    selectedPathes: string[],
): WorkflowOption[] => {
    const filteredOptions = [];
    const filterOptions = (optionsData: WorkflowOption[], pathes: string[]) => {
        for (const option of optionsData) {
            if (option.connectorSubSelection.length) {
                filterOptions(option.connectorSubSelection, pathes);
            }
            if (pathes.includes(option.path) || option.connectorType === 'ALWAYS') {
                filteredOptions.push(option);
            }
        }
    };
    filterOptions(options, selectedPathes);
    return filteredOptions;
};

const standardizeReleaseData = (
    data: InitialReleaseDataStructure<InitialReleaseOption, InitialReleaseDocument>[],
): [InitialReleaseData, ReleaseTabsData[]] => {
    const newDocuments = data.find(item => item.releasetype === 'new_document') ?? null;
    const newIndexes = data.filter(item => item.releasetype === 'new_index') ?? null;
    const invalidDocuments = data.find(item => item.releasetype === 'invalid') ?? null;

    if (newDocuments) {
        newDocuments.documents = newDocuments.documents.map(item => {
            return { ...item, title: getDocumentTitle(item) };
        });
    }
    if (newIndexes.length) {
        newIndexes.forEach(item => {
            item.documents = item.documents.map(document => {
                return { ...document, title: getDocumentTitle(document) };
            });
        });
    }
    if (invalidDocuments) {
        invalidDocuments.documents = invalidDocuments?.documents?.map(item => {
            return { ...item, title: getDocumentTitle(item) };
        });
    }

    const tabsData = [...newIndexes, newDocuments].filter(Boolean).map(item => {
        const options =
            item.options.map((opt, index) =>
                addPathInitialRelease(opt, `${opt.workflowTitle}<|>options[${index}]`),
            ) ?? [];

        return {
            ...{
                ...item,
                options,
            },
            isDisabled: false,
            isCompleted: false,
        };
    });

    return [
        {
            newDocuments: newDocuments,
            newIndexes: newIndexes,
            invalidDocuments: invalidDocuments,
        },
        tabsData,
    ];
};

const getFilteredReleaseOptions = (
    optionsData: any[],
    selectedPathes: string[],
): InitialReleaseOption => {
    for (const option of optionsData) {
        if (option.workflowTask?.length) {
            const selectedStartTask = option.workflowTask.find(x =>
                selectedPathes.includes(x.path),
            );
            if (selectedStartTask === undefined) continue;
            return {
                ...option,
                workflowTaskId: selectedStartTask.workflowTaskId,
            };
        }
        if (selectedPathes.includes(option.path)) {
            return option;
        }
    }
};

export const standardizeAdditionalInputResponse = (
    additionalInputResponse: string,
): IAdditionalInputResponse => {
    const response: IAdditionalInputResponse = safeJsonParse(additionalInputResponse, null);
    if (response === null) return null;
    response.dialogOptionChoice = response.dialogOptionChoice.map(item => ({
        value: item.value,
        alias: item.alias ?? item.value,
    }));
    return response;
};

/**
 * Initial slice state value.
 */
const initialState: IProjectWorkflowState = {
    wasPasswordRequestSuccessful: null,
    isPasswordDialogOpen: false,
    isMainDialogOpen: false,
    isForwardingQualityGatePassed: false,
    isInitialReleaseDialogOpen: false,
    isLoadingData: false,
    completedWorkflowForwardGroup: false,
    completedWorkflowInitialRelease: false,
    docUnIdsForForwardDialog: null,
    docUnIdsFailedForward: [],
    isDirectForward: false,
    totalDirectForwarded: null,
    loading: false,
    error: null,
    nextOptionsData: null,
    selectedTab: null,
    initialReleaseData: null,
    selectedReleaseTab: null,
    releaseOptionsData: null,
    hasPassword: null,
    additionalInput: null,
};

const reducers = {
    /**
     * Updates the active display type.
     * @param state - Slice state object.
     * @param action - Action to execute against the state.
     */
    resetWasPasswordRequestSuccessful(state: IProjectWorkflowState) {
        state.wasPasswordRequestSuccessful = null;
    },

    resetHasPassword(state: IProjectWorkflowState) {
        state.hasPassword = null;
    },

    openPasswordDialog(state: IProjectWorkflowState, action: IAction<boolean>) {
        state.isPasswordDialogOpen = action.payload;
    },

    resetAfterDirectForward(state: IProjectWorkflowState) {
        state.isDirectForward = false;
        state.totalDirectForwarded = null;
        state.docUnIdsFailedForward = [];
    },

    closeMainDialog(state: IProjectWorkflowState) {
        state.isMainDialogOpen = false;
        state.completedWorkflowForwardGroup = false;
        state.docUnIdsFailedForward = null;
        state.additionalInput = null;
        state.nextOptionsData = null;
        state.isForwardingQualityGatePassed = false;
    },

    closeInitialReleaseDialog(state: IProjectWorkflowState) {
        state.isInitialReleaseDialogOpen = false;
        state.completedWorkflowInitialRelease = false;
        state.docUnIdsForForwardDialog = null;
        state.releaseOptionsData = null;
    },

    goToNextForwardingTab(state: IProjectWorkflowState) {
        state.nextOptionsData[state.selectedTab].isCompleted = true;
        state.selectedTab++;
        state.completedWorkflowForwardGroup = false;
    },

    onNextInitialRelease(state: IProjectWorkflowState) {
        state.releaseOptionsData[state.selectedReleaseTab].isCompleted = true;
        state.selectedReleaseTab++;
    },

    removeDocumentFromWorkflow(
        state: IProjectWorkflowState,
        action: IAction<{ documentUniId: string; selectedTab: number; areaName: string }>,
    ) {
        const data = state.nextOptionsData;
        const tab = action.payload.selectedTab;

        const documents = data[tab].documents;
        data[tab].documents = documents.filter(
            doc => doc.universalId !== action.payload.documentUniId,
        );

        if (data[tab].documents.length === 0) {
            data[tab].isDisabled = true;
        }
    },

    removeDocumentFromInitialWorkflow(
        state: IProjectWorkflowState,
        action: IAction<{ documentUniId: string; selectedTab: number; areaName: string }>,
    ) {
        const data = state.releaseOptionsData;
        const tab = action.payload.selectedTab;

        const documents = data[tab].documents;
        data[tab].documents = documents.filter(doc => doc.docUnId !== action.payload.documentUniId);

        if (data[tab].documents.length === 0) {
            data[tab].isDisabled = true;
        }
    },

    passForwardingQualityGate(state: IProjectWorkflowState) {
        state.isForwardingQualityGatePassed = true;
    },
};
/**
 * Collection of the thunks for this slice, along with their reducer functions.
 */
const thunks = {
    /**
     * Check if password exists
     */
    checkIfPasswordIsSet: async (replicaId: string): Promise<boolean> => {
        try {
            const url = urlWorkflowPasswordValidation.replace(urlParamReplicaId, replicaId);
            const request: RequestInit = { method: 'POST' };
            const response = await apiFetch(url, request);
            return response.ok
                ? (await response.json()).isActive
                : Promise.reject(new Error(response.status.toString()));
        } catch (e) {
            return Promise.reject(e as Error);
        }
    },

    /**
     * Validate password on backend
     */
    validatePassword: async (
        {
            replicaId,
            password,
        }: {
            replicaId: string;
            password: string;
        },
        thunkApi,
    ): Promise<boolean> => {
        try {
            const formData = new FormData();
            formData.append('password', password);

            const request: RequestInit = {
                method: 'POST',
                body: formData,
            };

            const url = urlWorkflowPasswordValidation.replace(urlParamReplicaId, replicaId);
            const response = await apiFetch(url, request);

            return response.ok
                ? (await response.json()).wasSuccessful
                : Promise.reject(new Error(await response.json()));
        } catch (e) {
            return Promise.reject(e as Error);
        }
    },

    /**
     * Get data for Main dialog
     */
    /** GET request to backend to get next options for Main dialog */
    getDataForMainDialog: async (
        config: {
            documentIds: string[];
            replicaId: string;
        },
        thunkApi,
    ): Promise<
        | ForwardDataStructure<WorkflowOption, WorkflowDocument>[]
        | IAdditionalInputResponseData
        | IDirectForwardResponseData
    > => {
        try {
            const { replicaId, documentIds } = config;
            const request: RequestInit = {
                headers: {
                    universalIds: documentIds.join('|'),
                },
            };

            const url = urlWorkflowNext.replace(urlParamReplicaId, replicaId);
            const response = await apiFetch(url, request);

            thunkApi.dispatch(resetFileUploadState());
            thunkApi.dispatch(resetUtilsFileUploadState());
            GlobalVariables.resetFiles();

            return response.ok
                ? Promise.resolve(await response.json())
                : Promise.reject(new Error(await response.text()));
        } catch (e) {
            return Promise.reject(e as Error);
        }
    },

    /**
     * Get data for Initial Release dialog
     */
    getDataForInitialReleaseDialog: async (config: {
        documentIds: string[];
        replicaId: string;
    }): Promise<InitialReleaseDataStructure<InitialReleaseOption, InitialReleaseDocument>[]> => {
        const { replicaId, documentIds } = config;
        try {
            const request: RequestInit = {
                headers: {
                    universalIds: documentIds.join('|'),
                },
            };

            const url = urlWorkflowRelease.replace(urlParamReplicaId, replicaId);
            const response = await apiFetch(url, request);

            return response.ok
                ? Promise.resolve(await response.json())
                : Promise.reject(new Error(await response.text()));
        } catch (e) {
            return Promise.reject(e as Error);
        }
    },

    onConfirm: async (
        { pathes, replicaId },
        thunkApi,
    ): Promise<IAdditionalInputResponseData | string[]> => {
        const { nextOptionsData, selectedTab } = thunkApi.getState().projectWorkflow;
        const workflowForward: ForwardRequestStructure<WorkFlowOptionRequest, string>[] = [];

        const { documents, options } = nextOptionsData[selectedTab];
        const filteredOptions = getFilteredOptions(options, pathes);
        if (filteredOptions.length && documents.length) {
            workflowForward.push({
                docUnId: documents.map(item => item.universalId),
                option: filteredOptions.map(item => ({
                    connectorId: item.connectorId,
                    connectorSignStatus: item.connectorSignStatus,
                })),
            });
        }
        if (workflowForward.length === 0) {
            return Promise.resolve([]);
        }

        try {
            const formData = new FormData();
            formData.append('forwardSelection', JSON.stringify(workflowForward));

            const request: RequestInit = {
                method: 'POST',
                body: formData,
            };
            const url = urlWorkflowForward.replace(urlParamReplicaId, replicaId);
            const response = await apiFetch(url, request);

            return response.ok
                ? Promise.resolve(await response.json())
                : Promise.reject(new Error(response.status.toString()));
        } catch (e) {
            return Promise.reject(e as Error);
        }
    },

    onWorkflowTaskReply: async (
        { dialogResultButton, additionalInputState, replicaId }: IDynamicActionReplyArguments,
        thunkApi,
    ): Promise<
        | ForwardDataStructure<WorkflowOption, WorkflowDocument>[]
        | IAdditionalInputResponseData
        | IDirectForwardResponseData
        | string[]
    > => {
        const { taskId } = (thunkApi.getState() as IAppReduxStore).projectWorkflow.additionalInput;
        const { dialogType } = (thunkApi.getState() as IAppReduxStore).projectWorkflow
            .additionalInput.response;

        let resultValues;

        if (
            dialogType === AdditionalInputTypeEnum.UploadFiles &&
            [IAdditionalInfoResultButtonEnum.Yes, IAdditionalInfoResultButtonEnum.Ok].includes(
                dialogResultButton,
            )
        ) {
            const filesArray = GlobalVariables.getAllFiles();

            if (filesArray.length) {
                await thunkApi.dispatch(uploadFiles(filesArray));
            }

            const { filesPathes } = (thunkApi.getState() as IAppReduxStore).fileUpload;
            resultValues = filesPathes;
        } else {
            resultValues = additionalInputState;
        }

        const content = {
            dialogResultButton: dialogResultButton,
            dialogResultValues: resultValues,
        };

        try {
            const formData = new FormData();
            formData.append('taskId', taskId);
            formData.append('content', JSON.stringify(content));

            const url = urlWorkflowTaskReply.replace(urlParamReplicaId, replicaId);
            const request: RequestInit = {
                method: 'POST',
                body: formData,
            };
            const response = await apiFetch(url, request);

            thunkApi.dispatch(resetFileUploadState());
            thunkApi.dispatch(resetUtilsFileUploadState());
            GlobalVariables.resetFiles();

            return response.ok
                ? Promise.resolve(await response.json())
                : Promise.reject(new Error(response.status.toString()));
        } catch (e) {
            return Promise.reject(e as Error);
        }
    },

    onConfirmInitialRelease: async ({ pathes, replicaId }, thunkApi): Promise<string[]> => {
        const state = thunkApi.getState();
        const { releaseOptionsData } = state.projectWorkflow;

        const workflowRelease: ReleaseRequestStructure[] = [];

         releaseOptionsData.forEach((tabData, index: number) => {
            const { documents } = tabData;
            const finalOptions = getFinalLevelReleaseOptions(state, index);
            const filteredOption = getFilteredReleaseOptions(finalOptions, Object.values(pathes));

            if (filteredOption) {
                for (const document of documents) {
                    let temp: ReleaseRequestStructure = {
                        docUnId: document.docUnId,
                        workflowId: filteredOption.workflowId,
                        workflowTaskId: filteredOption.workflowTaskId,
                    };

                    if (filteredOption['role']) {
                        temp = {
                            ...temp,
                            role: filteredOption['role'],
                        };
                    }

                    workflowRelease.push(temp);
                }
            }
        });

        try {
            const formData = new FormData();
            formData.append('releaseSelection', JSON.stringify(workflowRelease));

            const request: RequestInit = {
                method: 'POST',
                body: formData,
            };

            const url = urlWorkflowRelease.replace(urlParamReplicaId, replicaId);
            const response = await apiFetch(url, request);

            return response.ok
                ? Promise.resolve(await response.json())
                : Promise.reject(new Error(await response.text()));
        } catch (e) {
            return Promise.reject(e as Error);
        }
    },

    /**
     * Registers the 'extraReducers' functions for the slice's thunks.
     */
    _reducers: (builder: ActionReducerMapBuilder<IProjectWorkflowState>) => {
        /**
         * Reducers for the 'checkIfPasswordIsSet' thunk.
         */
        builder.addCase(checkIfPasswordIsSet.fulfilled, (state, action) => {
            state.isPasswordDialogOpen = action.payload;
            state.hasPassword = action.payload;
        });

        builder.addCase(checkIfPasswordIsSet.rejected, (state, action) => {
            state.loading = false;
            state.error = action.error;
        });

        /**
         * Reducers for the 'postValidatePassword' thunk.
         */
        builder.addCase(postValidatePassword.fulfilled, (state, action) => {
            state.wasPasswordRequestSuccessful = action.payload;
        });

        builder.addCase(postValidatePassword.rejected, (state, action) => {
            state.loading = false;
            state.error = action.error;
            state.wasPasswordRequestSuccessful = false;
        });

        /**
         * Reducers for the 'getDataForMainDialog' thunk.
         */
        builder.addCase(getDataForMainDialog.pending, (state: Draft<IProjectWorkflowState>) => {
            state.loading = true;
            state.isLoadingData = true;
            state.error = null;
        });

        builder.addCase(
            getDataForMainDialog.fulfilled,
            (
                state: Draft<IProjectWorkflowState>,
                action: IAction<
                    | ForwardDataStructure<WorkflowOption, WorkflowDocument>[]
                    | IAdditionalInputResponseData
                    | IDirectForwardResponseData
                >,
            ) => {
                state.loading = false;
                state.isLoadingData = false;
                state.additionalInput = null;

                if (isDirectForwardDataStructure(action.payload)) {
                    state.isDirectForward = true;
                    state.totalDirectForwarded = action.payload.totalDocUnIds.length;
                    state.docUnIdsFailedForward = action.payload.failedDocUnIds;
                    return;
                }

                state.isMainDialogOpen = true;

                if (
                    Array.isArray(action.payload) &&
                    isArrayOfForwardDataStructure(action.payload)
                ) {
                    state.selectedTab = 0;
                    state.completedWorkflowForwardGroup = false;

                    state.nextOptionsData = action.payload.map(item => {
                        return {
                            ...{
                                ...item,
                                options: item.options.map((opt, index) =>
                                    addPath(opt, `${opt.currentTask}<|>options[${index}]`),
                                ),
                            },
                            isDisabled: false,
                            isCompleted: false,
                        };
                    });
                } else if (isAdditionalInputResponseData(action.payload)) {
                    state.additionalInput = {
                        ...action.payload,
                        response: standardizeAdditionalInputResponse(action.payload.response),
                    };
                }
            },
        );

        builder.addCase(getDataForMainDialog.rejected, (state, action) => {
            state.loading = false;
            state.isLoadingData = false;
            state.selectedTab = 0;
            state.completedWorkflowForwardGroup = false;
            state.nextOptionsData = [
                {
                    documents: [],
                    options: [],
                    isDisabled: true,
                    isCompleted: false,
                },
            ];

            state.additionalInput = null;
            state.error = action.error;
        });

        /**
         * Reducers for the 'onWorkflowTaskReply' thunk.
         */
        builder.addCase(onWorkflowTaskReply.pending, (state: Draft<IProjectWorkflowState>) => {
            state.loading = true;
            state.isLoadingData = true;
            state.error = null;
            state.isMainDialogOpen = true;
        });

        builder.addCase(
            onWorkflowTaskReply.fulfilled,
            (
                state: Draft<IProjectWorkflowState>,
                action: IAction<
                    | ForwardDataStructure<WorkflowOption, WorkflowDocument>[]
                    | IAdditionalInputResponseData
                    | IDirectForwardResponseData
                    | string[]
                >,
            ) => {
                state.loading = false;
                state.isLoadingData = false;
                state.additionalInput = null;

                if (isDirectForwardDataStructure(action.payload)) {
                    state.isDirectForward = true;
                    state.completedWorkflowForwardGroup = true;
                    state.totalDirectForwarded = action.payload.totalDocUnIds.length;
                    state.docUnIdsFailedForward = action.payload.failedDocUnIds;
                    return;
                }

                if (Array.isArray(action.payload)) {
                    if (isArrayOfString(action.payload)) {
                        state.docUnIdsFailedForward = action.payload;
                        state.completedWorkflowForwardGroup = true;
                    } else {
                        state.selectedTab = 0;
                        state.completedWorkflowForwardGroup = false;

                        state.nextOptionsData = action.payload.map(item => {
                            return {
                                ...item,
                                options: item.options.map((opt, index) =>
                                    addPath(opt, `${opt.currentTask}<|>options[${index}]`),
                                ),
                                isDisabled: false,
                                isCompleted: false,
                            };
                        });
                    }
                } else if (isAdditionalInputResponseData(action.payload)) {
                    state.additionalInput = {
                        ...action.payload,
                        response: standardizeAdditionalInputResponse(action.payload.response),
                    };
                }
            },
        );

        builder.addCase(onWorkflowTaskReply.rejected, (state, action) => {
            state.loading = false;
            state.isLoadingData = false;
            state.selectedTab = 0;
            state.completedWorkflowForwardGroup = false;
            state.nextOptionsData = [
                {
                    documents: [],
                    options: [],
                    isDisabled: true,
                    isCompleted: false,
                },
            ];
            state.additionalInput = null;

            state.error = action.error;
        });

        /**
         * Reducers for the 'onConfirm' thunk.
         */
        builder.addCase(onConfirm.pending, (state: Draft<IProjectWorkflowState>) => {
            state.loading = true;
            state.isLoadingData = true;
            state.error = null;
        });

        builder.addCase(
            onConfirm.fulfilled,
            (
                state: Draft<IProjectWorkflowState>,
                action: IAction<IAdditionalInputResponseData | string[]>,
            ) => {
                state.additionalInput = null;
                state.isLoadingData = false;
                state.loading = false;

                if (Array.isArray(action.payload) && isArrayOfString(action.payload)) {
                    state.docUnIdsFailedForward = action.payload;
                    state.completedWorkflowForwardGroup = true;
                } else if (isAdditionalInputResponseData(action.payload)) {
                    state.additionalInput = {
                        ...action.payload,
                        response: standardizeAdditionalInputResponse(action.payload.response),
                    };
                }
            },
        );

        builder.addCase(onConfirm.rejected, (state, action) => {
            state.loading = false;
            state.isLoadingData = false;
            state.error = action.error;
            state.additionalInput = null;
        });

        /**
         * Reducers for the 'onConfirmInitialRelease' thunk.
         */
        builder.addCase(onConfirmInitialRelease.pending, (state: Draft<IProjectWorkflowState>) => {
            state.loading = true;
            state.error = null;
        });

        builder.addCase(
            onConfirmInitialRelease.fulfilled,
            (state: Draft<IProjectWorkflowState>, action: IAction<string[]>) => {
                state.docUnIdsForForwardDialog = action.payload;
                state.loading = false;
                state.completedWorkflowInitialRelease = true;
            },
        );

        builder.addCase(onConfirmInitialRelease.rejected, (state, action) => {
            state.loading = false;
            state.error = action.error;
        });

        /**
         * Reducers for the 'getDataForInitialReleaseDialog' thunk.
         */
        builder.addCase(
            getDataForInitialReleaseDialog.pending,
            (state: Draft<IProjectWorkflowState>) => {
                state.loading = true;
                state.error = null;
            },
        );

        builder.addCase(getDataForInitialReleaseDialog.fulfilled, (state, action) => {
            state.loading = false;
            state.isInitialReleaseDialogOpen = true;

            [state.initialReleaseData, state.releaseOptionsData] = standardizeReleaseData(
                action.payload,
            );
            state.selectedReleaseTab = 0;
        });

        builder.addCase(getDataForInitialReleaseDialog.rejected, (state, action) => {
            state.loading = false;
            state.error = action.error;
        });
    },
};

export const checkIfPasswordIsSet = createAsyncThunk(
    `${urlWorkflowPasswordValidation}-isSet`,
    thunks.checkIfPasswordIsSet,
);

export const postValidatePassword = createAsyncThunk(
    `${urlWorkflowPasswordValidation}-validate`,
    thunks.validatePassword,
);

export const getDataForMainDialog = createAsyncThunk(
    `${urlWorkflowNext}`,
    thunks.getDataForMainDialog,
);

export const getDataForInitialReleaseDialog = createAsyncThunk(
    `data-for-initial-release`,
    thunks.getDataForInitialReleaseDialog,
);

export const onConfirm = createAsyncThunk(`${urlWorkflowForward}`, thunks.onConfirm);

export const onConfirmInitialRelease = createAsyncThunk(
    `on-confirm-initial-release`,
    thunks.onConfirmInitialRelease,
);

export const onWorkflowTaskReply = createAsyncThunk(
    `${urlWorkflowTaskReply}`,
    thunks.onWorkflowTaskReply,
);

// Added typeguards to avoid runtime errors
const isArrayOfString = (arr: unknown): arr is string[] => {
    if (!Array.isArray(arr)) return false;

    if (arr.length === 0) return true;

    return arr.every(item => typeof item === 'string');
};
const isArrayOfForwardDataStructure = (
    arr: object[],
): arr is ForwardDataStructure<WorkflowOption, WorkflowDocument>[] => {
    return arr.every((item): item is ForwardDataStructure<WorkflowOption, WorkflowDocument> => {
        return 'options' in item;
    });
};

const isDirectForwardDataStructure = (response: object): response is IDirectForwardResponseData => {
    return (
        'directForward' in response && 'totalDocUnIds' in response && 'failedDocUnIds' in response
    );
};

export const isAdditionalInputResponseData = (
    response: object,
): response is IAdditionalInputResponseData => {
    return 'taskId' in response;
};
/**
 * Interface that describes the state object for this redux slice.
 */
export interface IProjectWorkflowState extends SliceProps {
    wasPasswordRequestSuccessful: boolean;
    isPasswordDialogOpen: boolean;
    isLoadingData: boolean;
    isDirectForward: boolean;
    totalDirectForwarded: number;
    completedWorkflowForwardGroup: boolean;
    completedWorkflowInitialRelease: boolean;
    docUnIdsForForwardDialog: string[];
    docUnIdsFailedForward: string[];
    isMainDialogOpen: boolean;
    isForwardingQualityGatePassed: boolean;
    isInitialReleaseDialogOpen: boolean;
    nextOptionsData: TabsData[];
    selectedTab: number;
    initialReleaseData: InitialReleaseData;
    selectedReleaseTab: number;
    releaseOptionsData: ReleaseTabsData[];
    hasPassword: boolean;
    additionalInput: IAdditionalInput;
}

export interface WorkflowRequest {
    workflowForward: ForwardRequestStructure<WorkFlowOptionRequest, string>[];
}

interface WorkflowDocument {
    universalId: string;
    documentNumber: string;
    docIndex: string;
    subject: string;
}

interface ForwardDataStructure<TOption, TDocs> {
    documents: TDocs[];
    options: TOption[];
}

interface ForwardRequestStructure<TOption, TDocs> {
    docUnId: TDocs[];
    option: TOption[];
}

interface WorkFlowOptionRequest {
    connectorId: string;
    connectorSignStatus: string;
}

export interface WorkflowOption extends WorkFlowOptionRequest {
    optionId: string;
    path: string;
    currentTask: string;
    connectorMultipleForward: string;
    connectorDescription: string;
    connectorSignStatus: string;
    connectorStandardSelection: boolean;
    connectorSubSelection: WorkflowOption[];
    connectorType: 'ALWAYS' | 'EXCLUSIVE_CHOICE' | 'MULTIPLE_CHOICE';
}
export interface TabsData extends ForwardDataStructure<WorkflowOption, WorkflowDocument> {
    isDisabled: boolean;
    isCompleted: boolean;
}

export enum ReleaseTypeEnum {
    NewDocument = 'new_document',
    NewIndex = 'new_index',
    Invalid = 'invalid',
}

export type InitialReleaseType = ReleaseTypeEnum;

interface InitialReleaseDataStructure<TOption, TDoc> {
    releasetype: InitialReleaseType;
    documents: TDoc[];
    options: TOption[];
}

export interface InitialReleaseData {
    newDocuments: InitialReleaseDataStructure<InitialReleaseOption, InitialReleaseDocument>;
    newIndexes: InitialReleaseDataStructure<InitialReleaseOption, InitialReleaseDocument>[];
    invalidDocuments: InitialReleaseDataStructure<InitialReleaseOption, InitialReleaseDocument>;
}

export interface InitialReleaseDocument {
    docUnId: string;
    docSubject: string;
    docNumber: string;
    docIndex: string;
    dbServer: string;
    dbReplicaId: string;
    messages: string[];
    title: string;
}
export interface InitialReleaseOption {
    workflowId: string;
    workflowTitle: string;
    workflowTaskId: string;
    workflowTaskTitle: string;
    workflowTask: InitialReleaseFinalOption[];
    workflowTaskDynamicRole?: string;
    path: string;
}

export interface InitialReleaseFinalOption {
    workflowId: string;
    workflowTaskId: string;
    workflowTaskTitle: string;
    workflowTaskDynamicRole?: string[];
    path: string;
    role?: InitialReleaseRoleOption[];
}

export interface InitialReleaseRoleOption {
    workflowId: string;
    workflowTaskId: string;
    workflowTaskTitle: string;
    path: string;
    role: string;
}

export interface ReleaseTabsData
    extends InitialReleaseDataStructure<InitialReleaseOption, InitialReleaseDocument> {
    isDisabled: boolean;
    isCompleted: boolean;
}

type ReleaseRequestStructure =
    | {
          docUnId: string;
          workflowId: string;
          workflowTaskId: string;
      }
    | {
          docUnId: string;
          workflowId: string;
          workflowTaskId: string;
          role: string;
      };

export enum AdditionalInputTypeEnum {
    ShowText = 'ShowText',
    ChoiceExclusive = 'ChoiceExclusive',
    ChoiceMultiple = 'ChoiceMultiple',
    InputText = 'InputText',
    InputDate = 'InputDate',
    UploadFiles = 'UploadFile',
    ShowUrlList = 'ShowUrlList',
    SelectViewCategoryList = 'SelectViewCategoryList',
    InputPassword = 'InputPassword',
    DigiSealSign = 'DigiSealSign',
}

export interface IAdditionalInputOptionChoice {
    value: string;
    alias?: string;
}

export interface IAdditionalInputResponseData {
    taskId: string;
    state: string;
    response: string;
}

interface IDirectForwardResponseData {
    directForward: boolean;
    totalDocUnIds: string[];
    failedDocUnIds: string[];
}

export interface IAdditionalInput {
    taskId: string;
    state: string;
    response: IAdditionalInputResponse;
}

interface IAdditionalInputResponse {
    dialogType: AdditionalInputTypeEnum;
    dialogTitle: string;
    dialogPrompt: string;
    dialogInformation: string;
    dialogButtonYes: boolean;
    dialogButtonNo: boolean;
    dialogButtonOk: boolean;
    dialogButtonCancel: boolean;
    dialogOptionChoice: IAdditionalInputOptionChoice[];
    dialogOptionDefault: string[];
}

/**
 * Create the redux slice.
 */
export const projectWorkflowDataSlice = createSlice({
    name: 'projectWorkflowData',
    extraReducers: thunks._reducers,
    initialState,
    reducers,
});

export const getFinalLevelReleaseOptions = (state: IAppReduxStore, index?: number) => {
    const releaseData = getCurrentTabsReleaseData(state, index);

    const roleOptions = releaseData.flatMap(option =>
        option.workflowTask.flatMap(task => task.role ?? []),
    );
    const nonRoleOptions = releaseData.flatMap(option => option.workflowTask.filter(x => !x.role));

    return [...roleOptions, ...nonRoleOptions];
};

export const getIsForwardingQualityGateOpened = (state: IAppReduxStore) => {
    const totalDocuments = getTotalForwardedDocuments(state);
    const selectedDocumentsCount =
        state.projectSelectedDocuments?.storage?.documentIds?.length || 0;
    const isDirectForward = state.projectWorkflow?.isDirectForward || false;
    const isForwardingQualityGatePassed =
        state.projectWorkflow?.isForwardingQualityGatePassed || false;
    const isDynamicDialogOpened = state.projectWorkflow?.additionalInput !== null;

    return (
        !isDynamicDialogOpened &&
        !isDirectForward &&
        !isForwardingQualityGatePassed &&
        selectedDocumentsCount > totalDocuments
    );
};

export const getForwardTabs = (state: IAppReduxStore) => {
    return (state.projectWorkflow?.nextOptionsData || []).map(item => {
        return {
            key: item.options[0]?.currentTask || '',
            name: item.options[0]?.currentTask || '',
            index: 0,
            isDisabled: item.isDisabled,
            isCompleted: item.isCompleted,
        };
    });
};

export const getReleaseTabs = (state: IAppReduxStore) => {
    return (state.projectWorkflow?.releaseOptionsData || []).map((item, index) => {
        return {
            key: `${item.releasetype} ${index}`,
            name: item.releasetype,
            index: item.releasetype === ReleaseTypeEnum.NewIndex && index > 0 ? index + 1 : 0,
            isDisabled: item.isDisabled,
            isCompleted: item.isCompleted,
        };
    });
};

export const getDocuments = (state: IAppReduxStore) => {
    const { selectedTab, nextOptionsData } = state.projectWorkflow;

    return (
        nextOptionsData?.[selectedTab]?.documents.map(document => {
            return {
                universalId: document.universalId,
                releasetype: null,
                title: getDocumentTitle({
                    docNumber: document.documentNumber,
                    docIndex: document.docIndex,
                    docSubject: document.subject,
                }),
                documentNumber: document.documentNumber,
            };
        }) ?? []
    );
};

export const getReleaseDocuments = (state: IAppReduxStore) => {
    const { selectedReleaseTab, releaseOptionsData } = state.projectWorkflow;

    return (
        releaseOptionsData?.[selectedReleaseTab]?.documents?.map(document => {
            return {
                title: document.title,
                releasetype: releaseOptionsData?.[selectedReleaseTab]?.releasetype,
                universalId: document.docUnId,
                documentNumber: null,
            };
        }) ?? []
    );
};

export const getTotalForwardedDocuments = (state: IAppReduxStore) => {
    const { nextOptionsData } = state.projectWorkflow;
    if (!nextOptionsData) {
        return 0;
    }

    return nextOptionsData.reduce((sum, element) => sum + (element?.documents?.length || 0), 0);
};

export const getTotalReleaseDocuments = (state: IAppReduxStore) => {
    const { releaseOptionsData } = state.projectWorkflow;
    if (!releaseOptionsData) {
        return 0;
    }

    return releaseOptionsData.reduce((sum, element) => sum + (element?.documents?.length || 0), 0);
};

export const getInitialIncompleteDocuments = (state: IAppReduxStore) => {
    const { initialReleaseData } = state.projectWorkflow;

    return (
        initialReleaseData?.invalidDocuments?.documents.map(document => {
            return {
                ...document,
                releasetype: initialReleaseData?.invalidDocuments.releasetype,
            };
        }) ?? []
    );
};

export const getInitialCompleteDocuments = (state: IAppReduxStore) => {
    const { initialReleaseData } = state.projectWorkflow;

    return [
        ...(initialReleaseData?.newDocuments?.documents.map(document => {
            return { ...document, releasetype: initialReleaseData?.newDocuments.releasetype };
        }) || []),
        ...(initialReleaseData?.newIndexes.flatMap(item =>
            item.documents.map(document => {
                return { ...document, releasetype: item.releasetype };
            }),
        ) || []),
    ];
};

export const getInitialTotalDocuments = (state: IAppReduxStore) => {
    const { initialReleaseData } = state.projectWorkflow;

    return (
        (initialReleaseData?.newDocuments?.documents?.length || 0) +
        initialReleaseData?.newIndexes?.reduce(
            (acc, item) => acc + (item.documents?.length || 0),
            0,
        ) +
        (initialReleaseData?.invalidDocuments?.documents?.length || 0)
    );
};

export const getNewIndexInitialOption = (state: IAppReduxStore) =>
    state.projectWorkflow.releaseOptionsData?.find(
        item => item.releasetype === ReleaseTypeEnum.NewIndex,
    )?.options?.[0];

export const getCurrentTabsForwardingData = (state: IAppReduxStore) => {
    const { selectedTab, nextOptionsData } = state.projectWorkflow;
    const filteredOutAlways = getCurrentTabsForwardingDataValidOptions(state);

    return filteredOutAlways.length ? filteredOutAlways : nextOptionsData?.[selectedTab]?.options;
};

export const getCurrentTabsForwardingDataValidOptions = (state: IAppReduxStore) => {
    const { selectedTab, nextOptionsData } = state.projectWorkflow;

    return (
        nextOptionsData?.[selectedTab]?.options.filter(item => item.connectorType !== 'ALWAYS') ??
        []
    );
};

export const getCurrentTabsReleaseData = (state: IAppReduxStore, tabIndex?: number) => {
    const { selectedReleaseTab, releaseOptionsData } = state.projectWorkflow;
    tabIndex = tabIndex ?? selectedReleaseTab;

    return (
        releaseOptionsData?.[tabIndex]?.options.map(item => {
            return { ...item };
        }) ?? []
    );
};

/**
 * Export functions from slice actions.
 */
export const {
    resetAfterDirectForward,
    resetWasPasswordRequestSuccessful,
    resetHasPassword,
    openPasswordDialog,
    passForwardingQualityGate,
    closeMainDialog,
    closeInitialReleaseDialog,
    removeDocumentFromWorkflow,
    removeDocumentFromInitialWorkflow,
    goToNextForwardingTab,
    onNextInitialRelease,
} = projectWorkflowDataSlice.actions;

/**
 * Export slice reducer as default.
 */
export default projectWorkflowDataSlice.reducer;
